import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { getPurchase } from '../services/purchasesService';
import { formatDate, formatMoney } from '../lib/format';
import { friendlyError } from '../lib/errors';
import LoadingState from '../components/ui/LoadingState';
import ErrorState from '../components/ui/ErrorState';
import EmptyState from '../components/ui/EmptyState';
import Badge from '../components/ui/Badge';

export default function PurchaseDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [compra, setCompra] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setCompra(await getPurchase(id));
    } catch (e) {
      setError(friendlyError(e));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) return <LoadingState label="Cargando compra…" />;
  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!compra) return <EmptyState title="Compra no encontrada" />;

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <p className="breadcrumb">
            <Link to="/admin/compras">Compras</Link> / {compra.serie}-{compra.correlativo}
          </p>
          <h1>
            {compra.serie}-{compra.correlativo}
          </h1>
          <p className="muted">
            {formatDate(compra.fechaEmision || compra.createdAt)} · {compra.proveedorRazonSocial || 'Proveedor'}
          </p>
        </div>
        <div className="row-actions">
          <button type="button" className="btn btn-ghost" onClick={() => navigate(-1)}>
            Volver
          </button>
          <Link className="btn btn-primary" to={`/admin/compras/${id}/editar`}>
            Editar
          </Link>
        </div>
      </header>

      <section className="stat-grid compact">
        <div className="stat-card">
          <p className="stat-label">Total</p>
          <p className="stat-value">{formatMoney(compra.total, compra.moneda)}</p>
        </div>
        <div className="stat-card">
          <p className="stat-label">Subtotal</p>
          <p className="stat-value" style={{ fontSize: '1.1rem' }}>
            {formatMoney(compra.subtotal, compra.moneda)}
          </p>
        </div>
        <div className="stat-card">
          <p className="stat-label">IGV</p>
          <p className="stat-value" style={{ fontSize: '1.1rem' }}>
            {formatMoney(compra.igv, compra.moneda)}
          </p>
        </div>
        <div className="stat-card">
          <p className="stat-label">Estado</p>
          <p className="stat-value" style={{ fontSize: '1rem' }}>
            <Badge tone="ok">{compra.estado || '—'}</Badge>
          </p>
        </div>
      </section>

      <section className="meta-grid">
        <div>
          <span className="muted">Tipo doc</span>
          <div>{compra.tipoDocumento || '—'}</div>
        </div>
        <div>
          <span className="muted">Proveedor RUC</span>
          <div>{compra.proveedorRuc || '—'}</div>
        </div>
        <div>
          <span className="muted">Proveedor</span>
          <div>{compra.proveedorRazonSocial || '—'}</div>
        </div>
        <div>
          <span className="muted">Categoría</span>
          <div>{compra.categoria || '—'}</div>
        </div>
        <div>
          <span className="muted">Moneda</span>
          <div>{compra.moneda || '—'}</div>
        </div>
        <div>
          <span className="muted">Descripción</span>
          <div>{compra.descripcion || '—'}</div>
        </div>
        {compra.archivoUrl ? (
          <div>
            <span className="muted">Adjunto</span>
            <div>
              <a href={compra.archivoUrl} target="_blank" rel="noreferrer">
                {compra.archivoNombre || 'Ver archivo'}
              </a>
            </div>
          </div>
        ) : null}
      </section>

      <div className="panel">
        <p className="muted">
          El ingreso de inventario no se hace automáticamente al guardar la compra.
          Usa <Link to="/admin/inventario">Inventario</Link> con <code>aumentarStock</code> (misma lógica que la app móvil).
        </p>
      </div>
    </div>
  );
}
